import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Users, Heart, AlertTriangle, CheckCircle } from 'lucide-react';
import { useProgress } from '../context/ProgressContext';
export function PeerRelationshipsPage() {
  const navigate = useNavigate();
  const {
    progress
  } = useProgress();
  const modules = [{
    id: 'healthy-friendships',
    title: 'What Makes a Good Friend?',
    icon: Users,
    description: 'Build strong, supportive friendships',
    color: 'from-blue-500 to-cyan-500',
    content: ['Good friends support you, celebrate your wins, and comfort you in losses', "They respect your boundaries and don't pressure you", 'Real friends are happy when you spend time with other people', 'They listen to you and care about your feelings', 'You can be yourself around them - no pretending needed', 'They apologize when they hurt you and try to do better']
  }, {
    id: 'red-flags',
    title: 'Friendship Red Flags',
    icon: AlertTriangle,
    description: 'Spot unhealthy friendship patterns',
    color: 'from-red-500 to-orange-500',
    content: ['They only contact you when they need something', 'They put you down or make fun of you in front of others', "They pressure you to do things you're uncomfortable with", 'They get jealous when you have other friends', 'They share your secrets or talk behind your back', "They never apologize or admit when they're wrong", 'You feel drained or anxious after spending time with them']
  }, {
    id: 'romantic-relationships',
    title: 'Healthy Dating',
    icon: Heart,
    description: 'Navigate romantic relationships safely',
    color: 'from-pink-500 to-rose-500',
    content: ['Healthy relationships have mutual respect, trust, and communication', 'Your partner should support your goals and friendships', "You should both have privacy - checking phones isn't love, it's control", 'Jealousy and possessiveness are red flags, not signs of love', "It's okay to disagree - what matters is how you resolve conflicts", 'You should feel safe, respected, and valued in the relationship', "If you're scared of your partner's reaction, that's a major red flag"]
  }, {
    id: 'setting-boundaries',
    title: 'Setting Boundaries',
    icon: CheckCircle,
    description: 'Learn to protect your energy and well-being',
    color: 'from-green-500 to-teal-500',
    content: ["It's okay to say no - you don't owe anyone an explanation", "You can end friendships that don't serve you anymore", "It's okay to need alone time or space from friends", 'You can choose not to share everything about your life', 'Real friends will respect your boundaries without making you feel guilty', "Setting boundaries isn't selfish - it's self-care", 'If someone violates your boundaries repeatedly, distance yourself']
  }];
  return <div className="min-h-screen w-full bg-gradient-to-br from-blue-50 via-cyan-50 to-teal-50 dark:from-slate-900 dark:via-blue-950 dark:to-cyan-950 pb-20 md:pb-8" data-id="element-773">
      <header className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm border-b border-blue-100 dark:border-slate-700 sticky top-0 z-10" data-id="element-774">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between" data-id="element-775">
          <button onClick={() => navigate('/home')} className="flex items-center space-x-2 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300" data-id="element-776">
            <ArrowLeft className="w-5 h-5" data-id="element-777" />
            <span className="font-medium" data-id="element-778">Home</span>
          </button>
          <h1 className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-779">
            Healthy Relationships
          </h1>
          <div className="w-20" data-id="element-780" />
        </div>
      </header>
      <div className="max-w-6xl mx-auto px-4 py-8" data-id="element-781">
        <div className="text-center mb-8" data-id="element-782">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-3" data-id="element-783">
            Build Healthy Relationships, {progress.preferences.name || 'Friend'}
            !
          </h2>
          <p className="text-gray-600 dark:text-gray-300 max-w-2xl mx-auto" data-id="element-784">
            Learn what makes friendships and relationships healthy, spot red
            flags, and build connections that lift you up!
          </p>
        </div>
        <div className="space-y-6" data-id="element-785">
          {modules.map(module => {
          const Icon = module.icon;
          return <div key={module.id} className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg overflow-hidden" data-id="element-786">
                <div className={`h-24 bg-gradient-to-br ${module.color} flex items-center px-6`} data-id="element-787">
                  <Icon className="w-10 h-10 text-white mr-4" data-id="element-788" />
                  <div data-id="element-789">
                    <h3 className="text-2xl font-bold text-white" data-id="element-790">
                      {module.title}
                    </h3>
                    <p className="text-white/90 text-sm" data-id="element-791">
                      {module.description}
                    </p>
                  </div>
                </div>
                <div className="p-6" data-id="element-792">
                  <ul className="space-y-3" data-id="element-793">
                    {module.content.map((item, index) => <li key={index} className="flex items-start space-x-3" data-id="element-794">
                        <div className={`w-6 h-6 bg-gradient-to-br ${module.color} rounded-full flex items-center justify-center flex-shrink-0 mt-0.5`} data-id="element-795">
                          <span className="text-white text-xs font-bold" data-id="element-796">
                            {index + 1}
                          </span>
                        </div>
                        <p className="text-gray-700 dark:text-gray-300" data-id="element-797">
                          {item}
                        </p>
                      </li>)}
                  </ul>
                </div>
              </div>;
        })}
        </div>
        <div className="mt-8 bg-blue-50 dark:bg-slate-800 border-l-4 border-blue-500 rounded-lg p-6" data-id="element-798">
          <h4 className="font-bold text-blue-900 dark:text-blue-300 mb-2" data-id="element-799">
            You Deserve Healthy Relationships! 💙
          </h4>
          <p className="text-blue-800 dark:text-blue-200" data-id="element-800">
            Healthy relationships - whether friendships or romantic - should
            make you feel supported, respected, and valued. If a relationship
            consistently makes you feel bad about yourself, it's okay to walk
            away. Your well-being matters most!
          </p>
        </div>
      </div>
    </div>;
}